/*
    Merrid Controls. Wszelkie prawa zastrzeżone.
    Plik zawiera kody Źródłowe będące własnością firmy Merrid Controls.
    Nie wolno ich rozpowszechniać w całości lub fragmentach bez wyraźnej zgody właściciela.
*/


import React, {useContext, useEffect, useState} from "react";

import {useTypedSelector} from "@Services/Hooks/UseTypedSelector";

import APPLICATION_ROUTER_INTERFACE from "@ROUTER/Interfaces/Router";
import ApplicationRouterContext from "@Contexts/Router";

import LOGGED_USER_INTERFACE from "@Store/Interfaces/Authentication/User";


import {Redirect} from "react-router-dom";


import BlockContentHeaderComponent from "@Components/UI/Containers/Block/Header";
import BlockContentContainerComponent from "@Components/UI/Containers/Block";


import SettingsSMSCodeVerificationComponent from "@UIPages/Authenticated/Settings/_Parts/Verification";
import USER_INTERFACE from "@Services/Application/Interfaces/Users/User";
import {ERROR_SYSTEM_MESSAGE_INTERFACE} from "@Services/Application/Interfaces/_Messages/Errors";
import CHANGE_API_REQUEST_STATUS_ACTION from "@Actions/Interface/ChangeApiRequestStatus";
import DEFAULT_REQUEST_HEADERS from "@Services/Interfaces/Http/Request/Headers";
import {HTTP_ERROR_RESPONSE_INTERFACE} from "@Services/Interfaces/Http/Response/Error";
import {useDispatch} from "react-redux";
import ErrorMessageComponent from "@Components/UI/Communicates/Messages/Error";
import ApplicationApiContext from "@Contexts/Api";
import {Col, Row} from "react-bootstrap";
import {BiTrash} from "react-icons/all";
import DefaultMessageComponent from "@Components/UI/Communicates/Messages/Default";
import AUTHORIZATION_TOKEN_INTERFACE from "@Reducers/Api/RequestToken";
import REST_API_REQUEST from "@Services/Utilities/Requests";


type DisconnectWithOperatorPageProps = {};


export const SettingsDisconnectWithOperatorPageComponent: React.FC<DisconnectWithOperatorPageProps> = (): JSX.Element | null => {


    /** @const Global State Actions Dispatcher */
    const DISPATCHER = useDispatch();


    const LOGGED_USER: LOGGED_USER_INTERFACE = useTypedSelector(
        (state): ReturnType<any> => state?.authentication
    )?.loggedUser || null;

    const ROUTER: APPLICATION_ROUTER_INTERFACE = useContext(ApplicationRouterContext);


    /** @const {string} Global Rest Api Root URL Address */
    const REST_API_URL = useContext(ApplicationApiContext)?.API_ROOT_URL || null;

    /** @const {object} REST_API_DATA - Application State Store Rest Api Data */
    const REST_API_DATA:ReturnType<any> = useTypedSelector(
        (state) => state.api
    );

    /** @const {} REST_API_REQUEST_TOKEN - Application Rest Api Request Token */
    const REST_API_REQUEST_TOKEN: AUTHORIZATION_TOKEN_INTERFACE = REST_API_DATA?.requestToken || null;


    const [USER_AUTHENTICATED_STATUS, SET_USER_AUTHENTICATED_STATUS] = useState<boolean>(false);

    const CHANGE_USER_AUTHENTICATED_STATUS = (response: any) => {
        if (response.OperationGUID) SET_USER_AUTHENTICATED_STATUS(true);
    }


    /** @var {[object, function]} State - User Profile Data Object, User Profile Data Object Setter */
    const [USER_PROFILE_DATA, SET_USER_PROFILE_DATA] = useState<USER_INTERFACE | null>(null);
    /** @var {[boolean, function]} State - User Profile Component Error, User Profile Component Error Setter */
    const [USER_PROFILE_ERROR, SET_USER_PROFILE_ERROR] = useState<ERROR_SYSTEM_MESSAGE_INTERFACE | null>(null);


    const [USER_PROFILE_MESSAGE, SET_USER_PROFILE_MESSAGE] = useState<string>("");


    /** @hook {didMount, didUpdate} Get Terminals List from REST API */
    useEffect(() => {

        if (!USER_AUTHENTICATED_STATUS) return;

        /* Check if Monitory Note ID, API URL Present, User Logged */
        if (!REST_API_URL || !LOGGED_USER?.userID) return; // No Valid Data - Stop

        /** @const USER_PROFILE_API_URL User Profile REST API Endpoint URL */
        const USER_PROFILE_API_URL: string = REST_API_URL + "/User/Get";

        /** @const USER_PROFILE_REQUEST_DATA User Profile Request Data Object */
        const USER_PROFILE_REQUEST_DATA = {
            UserId: LOGGED_USER?.userID, // Logged User ID
            ContentType: "G" // Data Return Mode - Main User Data
        };

        /** @async {function} User Profile Data Request Handler */
        const GET_USER_PROFILE_DATA = async (): Promise<void | Function> => {
            /* Change Global Rest Api Request Status Action */
            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true)); // Set Status to true

            try {

                const USER_OPERATORS_RESPONSE = await REST_API_REQUEST(
                    USER_PROFILE_API_URL,
                    USER_PROFILE_REQUEST_DATA,
                    REST_API_REQUEST_TOKEN
                );

                if (USER_OPERATORS_RESPONSE.status === 200) {
                    const CONTENT = await USER_OPERATORS_RESPONSE.text();
                    if (CONTENT.trim()) {
                        SET_USER_PROFILE_DATA(JSON.parse(CONTENT));
                    }
                } else {
                    if (USER_OPERATORS_RESPONSE.status === 409) {
                        const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await USER_OPERATORS_RESPONSE.json();
                        SET_USER_PROFILE_ERROR({ErrorCode: CONTENT.ErrorCode, UserMessage: CONTENT.UserMessage});
                    } else {
                        SET_USER_PROFILE_ERROR({ErrorCode: USER_OPERATORS_RESPONSE.status, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
                    }
                }

            } catch (e) {
                SET_USER_PROFILE_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
            }

            /* Change Global Rest Api Request Status Action */
            DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

        };

        GET_USER_PROFILE_DATA();

    }, [USER_AUTHENTICATED_STATUS, USER_PROFILE_MESSAGE]);


    useEffect(() => {
        if (!USER_PROFILE_DATA) return;
        if (USER_PROFILE_DATA?.Driver && USER_PROFILE_DATA?.Driver?.length && USER_PROFILE_DATA?.Driver[0]) {
            if (USER_PROFILE_DATA?.Driver[0]?.Applicants && USER_PROFILE_DATA?.Driver[0]?.Applicants?.length === 1) {
                SET_USER_PROFILE_MESSAGE("Jesteś powiązany tylko z jednym przewoźnikiem. Odłączenie jest niemożliwe.");
            }
        }
        return () => {
            SET_USER_PROFILE_MESSAGE("");
        };
    }, [USER_PROFILE_DATA]);


    useEffect(() => {
        return () => {
            SET_USER_PROFILE_DATA(null);
            SET_USER_PROFILE_ERROR(null);
            SET_USER_PROFILE_MESSAGE("");
        }
    }, [])


    if (!LOGGED_USER || !LOGGED_USER?.userID) return <Redirect to={ROUTER.NOT_AUTHENTICATED.LOGIN.URL}/>;


    if (!ROUTER) return <Redirect to={"/404"}/>;


    if (!USER_AUTHENTICATED_STATUS) return <SettingsSMSCodeVerificationComponent verificationStatusHandler={CHANGE_USER_AUTHENTICATED_STATUS} />;


    // if (!USER_PROFILE_DATA) return null;


    const {Driver: USER_DATA} = USER_PROFILE_DATA || {};

    const USER_OPERATORS: any = USER_DATA && USER_DATA?.length > 0 ? USER_DATA[0]?.Applicants : [];

    const OperatorsList = USER_OPERATORS.map((operator: any) => {
        return (
            <Col key={operator?.MasterNo} xs={12} className={"user-operator-component user-operator" + (USER_OPERATORS.length < 2 ? " disabled" : "")}>
                <Row className={"user-operator-content"}>
                    <Col xs={USER_OPERATORS.length > 1 ? 10 : 12} className={"operator-name"}>
                        <span>{operator?.fdName || operator?.ShortName}</span>
                    </Col>
                    {USER_OPERATORS.length > 1 &&
	                <Col xs={2} className={"operator-remove"}>
                        <span onClick={() => OPERATOR_REMOVE_HANDLER(operator?.MasterNo)}>
                            <BiTrash/>
                        </span>
	                </Col>
                    }
                </Row>
            </Col>
        );
    });


    const OPERATOR_REMOVE_HANDLER = async (operatorId: string) => {

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(true));

        try {

            const DISCONNECT_WITH_OPERATOR_RESPONSE = await REST_API_REQUEST(
                `${REST_API_URL}/User/Applicant/Delete`,
                {UserId: LOGGED_USER?.userID, ApplicantMasterNo: operatorId},
                REST_API_REQUEST_TOKEN
            );

            if (DISCONNECT_WITH_OPERATOR_RESPONSE.status === 204) {
                SET_USER_PROFILE_MESSAGE("Zgłoszenie zostało przyjęte");
            } else {
                const CONTENT: HTTP_ERROR_RESPONSE_INTERFACE = await DISCONNECT_WITH_OPERATOR_RESPONSE.json();
                SET_USER_PROFILE_ERROR({ErrorCode: DISCONNECT_WITH_OPERATOR_RESPONSE.status, UserMessage: CONTENT.UserMessage});
            }

        } catch(e) {
            SET_USER_PROFILE_ERROR({ErrorCode: 500, UserMessage: "Wystąpił nieokreślony błąd - skontaktuj się z CDOK"});
        }

        DISPATCHER(CHANGE_API_REQUEST_STATUS_ACTION(false));

    };


    return (
        <BlockContentContainerComponent cssClasses={"settings-page-operators-component settings-page-operators settings-page-view-component"}>

            <BlockContentHeaderComponent headerText={"Odłącz przewoźnika"} />

            {!!USER_PROFILE_ERROR && <ErrorMessageComponent messageText={USER_PROFILE_ERROR.UserMessage} />}

            {!USER_PROFILE_ERROR &&
	        <Col xs={12} className={"default-section-header"}>
		        <span>{`Przewoźnicy`}</span>
	        </Col>
            }

            {!!USER_PROFILE_MESSAGE &&
            <Col xs={12} className={"no-list-elements-message default-system-message"}>
	            <span>{USER_PROFILE_MESSAGE}</span>
            </Col>
            }

            {USER_OPERATORS?.length < 1 && !USER_PROFILE_ERROR ?
            <Col xs={12} className={"no-list-elements-message default-system-message"}>
                <span>{`Brak powiązanych przewoźników`}</span>
            </Col>
            :
            OperatorsList
            }

        </BlockContentContainerComponent>
    );

}


export default SettingsDisconnectWithOperatorPageComponent;
